<template>
  <div class="task-board-wrapper">
    <div v-if="selectionMode" class="bulk-actions-bar bulk-actions-bar--active">
      <div class="bulk-left-group">
        <fluent-icon
          icon="checkbox-checked"
          size="18"
          class="bulk-checkbox-icon"
        />
        <div class="label-actions-trigger">
          <woot-button
            v-if="selectedCount > 0 && hasBulkMarkersPermission"
            v-tooltip="$t('BULK_ACTION.LABELS.ASSIGN_MARKERS')"
            size="small"
            color-scheme="primary"
            icon="tag"
            @click="toggleLabelActions"
          />
          <transition name="popover-animation">
            <label-actions
              v-if="showLabelActions"
              class="label-actions-box"
              @assign="assignLabels"
              @close="showLabelActions = false"
            />
          </transition>
          <woot-button
            v-if="selectedCount > 0 && hasBulkContactAttributesPermission"
            v-tooltip="$t('BULK_ACTION.CRM.CONTACT_ATTRIBUTES_TOOLTIP')"
            size="small"
            color-scheme="primary"
            icon="settings"
            @click="toggleAttributeActions"
          />
          <transition name="popover-animation">
            <label-actions
              v-if="showAttributeActions"
              class="label-actions-box"
              mode="attributes"
              :ticket-custom-attributes="contactCustomAttributes"
              @assign-custom-attributes="assignCustomAttributes"
              @close="showAttributeActions = false"
            />
          </transition>
        </div>
        <span class="bulk-selection-label">
          {{ selectedCount }} {{ bulkItemLabel }}(s)
          {{ $t('BULK_ACTION.CRM.SELECTED_SUFFIX') }}
        </span>
        <button
          v-if="selectedCount > 0 && selectedCount < selectableTicketIds.length"
          class="bulk-select-all-link"
          :disabled="isBulkUpdating"
          @click="selectAllVisibleTickets"
        >
          {{ $t('BULK_ACTION.CRM.SELECT_ALL') }}
          ({{ selectableTicketIds.length }})
        </button>
      </div>
      <div class="bulk-center-group">
        <p class="bulk-move-label">{{ $t('BULK_ACTION.CRM.MOVE_TO') }}</p>
        <select v-model="targetColumnId" class="bulk-column-select">
          <option :value="null" disabled>
            {{ $t('BULK_ACTION.CRM.CHOOSE_STAGE') }}
          </option>
          <option
            v-for="column in availableColumns"
            :key="column.id"
            :value="column.id"
          >
            {{ column.name }}
          </option>
        </select>
      </div>
      <div class="bulk-right-group">
        <woot-button
          size="small"
          variant="smooth"
          :disabled="isBulkUpdating"
          @click="exitSelectionMode"
        >
          {{ $t('BULK_ACTION.CRM.CANCEL') }}
        </woot-button>
        <woot-button
          size="small"
          color-scheme="primary"
          :is-loading="isBulkUpdating"
          :disabled="
            isBulkUpdating ||
              !hasBulkEditPermission ||
              !targetColumnId ||
              selectedCount === 0
          "
          @click="handleBulkMove"
        >
          {{ $t('BULK_ACTION.CRM.MOVE') }} {{ selectedCount }}
          {{ bulkItemLabel }}(s)
        </woot-button>
      </div>
    </div>
    <div class="container-fluid main-container scrollable-div">
      <div class="board-wrapper">
        <TaskList
          v-for="(listItem, index) in lists"
          :key="`${kanbanId}-${listItem.column.id || index}`"
          :index="index"
          :board="getBoard"
          :kanban-id="kanbanId"
          :list="listItem"
          :filters="filters"
          :selected-ticket-ids="selectedTicketIds"
          :locked-selection-column-id="lockedSelectionColumnId"
          :selection-mode="selectionMode"
          @toggle-ticket-selection="handleToggleTicketSelection"
        />
      </div>
    </div>

    <woot-modal
      :show.sync="showBulkLabelConfirmModal"
      :on-close="closeBulkLabelConfirmModal"
    >
      <div class="bulk-confirm-modal">
        <woot-modal-header
          :header-title="bulkLabelModalTitle"
          :header-content="bulkLabelModalDescription"
        >
          <p class="bulk-confirm-modal__description bulk-confirm-modal__labels">
            {{ $t('BULK_ACTION.CRM.MARKERS_LIST_LABEL') }}
            <strong>{{ pendingLabelAction.labels.join(', ') }}</strong>
          </p>
        </woot-modal-header>
        <div class="modal-footer delete-item bulk-confirm-modal__actions">
          <woot-button
            variant="clear"
            class="action-button btn-primary"
            :disabled="isBulkUpdating"
            @click="closeBulkLabelConfirmModal"
          >
            {{ $t('BULK_ACTION.CRM.BUTTON_KEEP') }}
          </woot-button>
          <woot-button
            :color-scheme="bulkLabelConfirmColorScheme"
            variant="smooth"
            class="action-button"
            :is-loading="isBulkUpdating"
            :disabled="isBulkUpdating"
            @click="confirmBulkLabelAction"
          >
            {{ bulkLabelConfirmText }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      :show.sync="showBulkAttributeConfirmModal"
      :on-close="closeBulkAttributeConfirmModal"
    >
      <div class="bulk-confirm-modal">
        <woot-modal-header
          :header-title="bulkAttributeModalTitle"
          :header-content="bulkAttributeModalDescription"
        >
          <p class="bulk-confirm-modal__description bulk-confirm-modal__labels">
            {{ $t('BULK_ACTION.CRM.ATTRIBUTE') }}:
            <strong>{{ pendingAttributeLabel }}</strong>
          </p>
        </woot-modal-header>
        <div class="modal-footer delete-item bulk-confirm-modal__actions">
          <woot-button
            variant="clear"
            class="action-button btn-primary"
            :disabled="isBulkUpdating"
            @click="closeBulkAttributeConfirmModal"
          >
            {{ $t('BULK_ACTION.CRM.BUTTON_KEEP') }}
          </woot-button>
          <woot-button
            :color-scheme="bulkAttributeConfirmColorScheme"
            variant="smooth"
            class="action-button"
            :is-loading="isBulkUpdating"
            :disabled="isBulkUpdating"
            @click="confirmBulkCustomAttributeAction"
          >
            {{ bulkAttributeConfirmText }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import TaskList from './TaskList';
import LabelActions from './LabelActions.vue';

export default {
  name: 'TaskBoard',
  mixins: [alertMixin],
  props: ['columns', 'kanbanId', 'filters', 'enterSelectionMode'],
  components: {
    TaskList,
    LabelActions,
  },
  data() {
    return {
      selectedTicketIds: [],
      lockedSelectionColumnId: null,
      targetColumnId: null,
      isBulkUpdating: false,
      selectionMode: !!this.enterSelectionMode,
      showLabelActions: false,
      showBulkLabelConfirmModal: false,
      pendingLabelAction: {
        labels: [],
        actionType: 'add',
      },
      showBulkAttributeConfirmModal: false,
      pendingAttributeAction: {
        key: '',
        label: '',
        value: '',
        actionType: 'add',
      },
      showAttributeActions: false,
    };
  },
  watch: {
    enterSelectionMode(val) {
      this.selectionMode = !!val;
      if (!val) {
        this.clearSelection();
        this.resetTarget();
      }
    },
    kanbanId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelection();
        this.resetTarget();
        this.selectionMode = false;
        this.$emit('exit-selection-mode');
      }
    },
  },
  computed: {
    ...mapGetters({
      kanbanList: 'getKanbanfunnels',
      currentUser: 'getCurrentUser',
    }),

    getDragOptions() {
      return {
        animation: '200',
        ghostClass: 'ghost',
        handle: '.board-header',
        group: 'kanban-board-lists',
      };
    },
    getBoard() {
      return this.columns.sort((a, b) => a.column.index - b.column.index);
    },
    lists() {
      return this.getBoard;
    },
    selectedCount() {
      return this.selectedTicketIds.length;
    },
    isBusinessOpportunity() {
      const kanban = this.kanbanList.find(
        obj => Number(obj.id) === Number(this.kanbanId)
      );
      return (
        kanban &&
        (kanban.type_opportunity === 'business' ||
          kanban.type_opportunity === 'both')
      );
    },
    bulkItemLabel() {
      return this.isBusinessOpportunity ? 'negócio' : 'ticket';
    },
    availableColumns() {
      return this.lists.map(item => ({
        id: item.column.id,
        name: item.column.name,
      }));
    },
    lockedSelectionColumnName() {
      if (!this.lockedSelectionColumnId) {
        return '';
      }
      const column = this.availableColumns.find(
        item => item.id === this.lockedSelectionColumnId
      );
      return column?.name || '';
    },
    selectableTicketIds() {
      if (!this.lockedSelectionColumnId) {
        return [];
      }

      const lockedColumn = this.lists.find(
        item => item.column.id === this.lockedSelectionColumnId
      );

      if (
        !lockedColumn ||
        !lockedColumn.column ||
        !lockedColumn.column.tickets
      ) {
        return [];
      }

      return lockedColumn.column.tickets.map(ticket => ticket.id);
    },
    hasBulkEditPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.crm_settings
      );
    },
    hasBulkContactAttributesPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.bulk_edit_contact_attributes_crm
      );
    },
    hasBulkMarkersPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.bulk_edit_markers_crm
      );
    },
    contactCustomAttributes() {
      const byModel = this.$store.getters['attributes/getAttributesByModel'];
      return byModel('contact_attribute') || [];
    },
    pendingAttributeLabel() {
      return (
        this.pendingAttributeAction.label || this.pendingAttributeAction.key
      );
    },
    isBulkLabelRemoveAction() {
      return this.pendingLabelAction.actionType === 'remove';
    },
    bulkLabelModalTitle() {
      return this.isBulkLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.CONFIRM_REMOVE_MARKERS_TITLE')
        : this.$t('BULK_ACTION.CRM.CONFIRM_ADD_MARKERS_TITLE');
    },
    bulkLabelModalDescription() {
      return this.isBulkLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.MARKERS_REMOVE_DESCRIPTION', {
            count: this.selectedCount,
            target: `${this.bulkItemLabel}(s)`,
          })
        : this.$t('BULK_ACTION.CRM.MARKERS_ADD_DESCRIPTION', {
            count: this.selectedCount,
            target: `${this.bulkItemLabel}(s)`,
          });
    },
    bulkLabelConfirmColorScheme() {
      return this.isBulkLabelRemoveAction ? 'alert' : 'primary';
    },
    bulkLabelConfirmText() {
      return this.isBulkLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.BUTTON_REMOVE')
        : this.$t('BULK_ACTION.CRM.BUTTON_ADD');
    },
    isBulkAttributeRemoveAction() {
      return this.pendingAttributeAction.actionType === 'remove';
    },
    bulkAttributeModalTitle() {
      return this.isBulkAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.CONFIRM_REMOVE_ATTRIBUTES_TITLE')
        : this.$t('BULK_ACTION.CRM.CONFIRM_UPDATE_ATTRIBUTES_TITLE');
    },
    bulkAttributeModalDescription() {
      return this.isBulkAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.ATTRIBUTES_REMOVE_DESCRIPTION', {
            count: this.selectedCount,
            target: `${this.bulkItemLabel}(s)`,
          })
        : this.$t('BULK_ACTION.CRM.ATTRIBUTES_UPDATE_DESCRIPTION', {
            count: this.selectedCount,
            target: `${this.bulkItemLabel}(s)`,
          });
    },
    bulkAttributeConfirmColorScheme() {
      return this.isBulkAttributeRemoveAction ? 'alert' : 'primary';
    },
    bulkAttributeConfirmText() {
      return this.isBulkAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.BUTTON_REMOVE')
        : this.$t('BULK_ACTION.CRM.BUTTON_ADD');
    },
  },
  methods: {
    handleToggleTicketSelection({ ticketId, selected, sourceColumnId }) {
      if (
        selected &&
        this.lockedSelectionColumnId &&
        this.lockedSelectionColumnId !== sourceColumnId
      ) {
        return;
      }

      const selectedSet = new Set(this.selectedTicketIds);

      if (selected) {
        if (!this.lockedSelectionColumnId) {
          this.lockedSelectionColumnId = sourceColumnId;
        }
        selectedSet.add(ticketId);
      } else {
        selectedSet.delete(ticketId);
      }

      this.selectedTicketIds = Array.from(selectedSet);

      if (this.selectedTicketIds.length === 0) {
        this.lockedSelectionColumnId = null;
      }
    },

    clearSelection() {
      this.selectedTicketIds = [];
      this.lockedSelectionColumnId = null;
    },

    resetTarget() {
      this.targetColumnId = null;
    },

    exitSelectionMode() {
      this.clearSelection();
      this.resetTarget();
      this.selectionMode = false;
      this.$emit('exit-selection-mode');
    },

    selectAllVisibleTickets() {
      if (this.selectedCount === 0 || this.selectableTicketIds.length === 0) {
        return;
      }
      this.selectedTicketIds = [...this.selectableTicketIds];
    },

    async handleBulkMove() {
      if (!this.hasBulkEditPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!this.targetColumnId || this.selectedTicketIds.length === 0) {
        return;
      }

      this.isBulkUpdating = true;

      try {
        await this.$store.dispatch('bulkUpdateTicketColumns', {
          kanbanfunnel_id: this.kanbanId,
          columns_id: this.targetColumnId,
          ticket_ids: this.selectedTicketIds,
        });

        this.showAlert(this.$t('BULK_ACTION.CRM.CARDS_MOVED_SUCCESS'));
        this.clearSelection();
        this.exitSelectionMode();
        this.$emit('reload-ticket-board');
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('BULK_ACTION.CRM.MOVE_CARDS_FAILED')
        );
      } finally {
        this.isBulkUpdating = false;
      }
    },

    toggleLabelActions() {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }
      this.showAttributeActions = false;
      this.showLabelActions = !this.showLabelActions;
    },

    async toggleAttributeActions() {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!this.contactCustomAttributes.length) {
        await this.$store.dispatch('attributes/get');
      }

      this.showLabelActions = false;
      this.showAttributeActions = !this.showAttributeActions;
    },

    assignLabels({ labels, actionType }) {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!labels || labels.length === 0 || this.selectedCount === 0) {
        return;
      }

      this.pendingLabelAction = {
        labels,
        actionType: actionType || 'add',
      };
      this.showBulkLabelConfirmModal = true;
    },

    closeBulkLabelConfirmModal() {
      if (this.isBulkUpdating) {
        return;
      }
      this.showBulkLabelConfirmModal = false;
      this.pendingLabelAction = {
        labels: [],
        actionType: 'add',
      };
    },

    assignCustomAttributes({ key, value, actionType }) {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        this.showAttributeActions = false;
        return;
      }

      if (!key || this.selectedCount === 0) {
        this.showAlert(this.$t('BULK_ACTION.CRM.ENTER_ATTRIBUTE_KEY'));
        return;
      }

      if (actionType === 'add' && value === '') {
        this.showAlert(this.$t('BULK_ACTION.CRM.ENTER_ATTRIBUTE_VALUE'));
        return;
      }

      const selectedAttribute = this.contactCustomAttributes.find(
        attribute => attribute.attribute_key === key
      );

      this.pendingAttributeAction = {
        key,
        label: selectedAttribute?.attribute_display_name || key,
        value,
        actionType: actionType || 'add',
      };

      this.showBulkAttributeConfirmModal = true;
    },

    closeBulkAttributeConfirmModal() {
      if (this.isBulkUpdating) {
        return;
      }
      this.showBulkAttributeConfirmModal = false;
      this.pendingAttributeAction = {
        key: '',
        label: '',
        value: '',
        actionType: 'add',
      };
    },

    async confirmBulkCustomAttributeAction() {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        this.closeBulkAttributeConfirmModal();
        return;
      }

      if (!this.pendingAttributeAction.key || this.selectedCount === 0) {
        this.closeBulkAttributeConfirmModal();
        return;
      }

      const customAttributesPayload = {
        [this.pendingAttributeAction.key]: this.pendingAttributeAction.value,
      };

      this.isBulkUpdating = true;

      try {
        await this.$store.dispatch('bulkUpdateContactAttributes', {
          kanbanfunnel_id: this.kanbanId,
          ticket_ids: this.selectedTicketIds,
          custom_attributes: customAttributesPayload,
          action_type: this.pendingAttributeAction.actionType,
        });

        this.showAlert(
          this.pendingAttributeAction.actionType === 'remove'
            ? this.$t('BULK_ACTION.CRM.ATTRIBUTE_REMOVED_SUCCESS')
            : this.$t('BULK_ACTION.CRM.ATTRIBUTE_UPDATED_SUCCESS')
        );

        this.closeBulkAttributeConfirmModal();
        this.showAttributeActions = false;
        this.clearSelection();
        this.exitSelectionMode();
        this.$emit('reload-ticket-board');
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('BULK_ACTION.CRM.ATTRIBUTE_UPDATE_FAILED')
        );
      } finally {
        this.isBulkUpdating = false;
      }
    },

    async confirmBulkLabelAction() {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        this.closeBulkLabelConfirmModal();
        return;
      }

      if (!this.pendingLabelAction.labels.length || this.selectedCount === 0) {
        this.closeBulkLabelConfirmModal();
        return;
      }

      this.isBulkUpdating = true;

      try {
        const labelIds = this.pendingLabelAction.labels
          .map(labelName => {
            const label = this.$store.getters['labelsCrm/getLabels'].find(
              l => l.title === labelName
            );
            return label ? label.id : null;
          })
          .filter(id => id !== null);

        if (labelIds.length === 0) {
          this.showAlert(this.$t('BULK_ACTION.CRM.NO_VALID_LABEL_SELECTED'));
          return;
        }

        await this.$store.dispatch('bulkUpdateTicketLabels', {
          kanbanfunnel_id: this.kanbanId,
          ticket_ids: this.selectedTicketIds,
          labels_ids: labelIds,
          action_type: this.pendingLabelAction.actionType,
        });

        this.showAlert(
          this.pendingLabelAction.actionType === 'remove'
            ? this.$t('BULK_ACTION.CRM.LABELS_REMOVED_SUCCESS')
            : this.$t('BULK_ACTION.CRM.LABELS_ASSIGNED_SUCCESS')
        );
        this.showLabelActions = false;
        this.closeBulkLabelConfirmModal();
        this.clearSelection();
        this.exitSelectionMode();
        this.$emit('reload-ticket-board');
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('BULK_ACTION.CRM.LABELS_UPDATE_FAILED')
        );
      } finally {
        this.isBulkUpdating = false;
      }
    },
  },
};
</script>

<style>
.task-board-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.task-board-wrapper > .main-container {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.bulk-actions-bar {
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  display: flex;
  gap: 0.75rem;
  margin: 0.5rem 1rem;
  min-height: 3.25rem;
  padding: 0.5rem 1.25rem;
}

.bulk-actions-bar--start {
  justify-content: flex-start;
  min-height: unset;
  border: none;
  background: transparent;
  padding: 0.25rem 1rem;
  margin: 0;
}

.bulk-actions-bar--active {
  justify-content: space-between;
  flex-wrap: wrap;
}

.bulk-checkbox-icon {
  color: #2563eb;
  flex-shrink: 0;
}

.bulk-selection-label {
  color: #1e3a5f;
  font-weight: 700;
  font-size: var(--font-size-small);
  white-space: nowrap;
  margin: 0;
}

.bulk-select-all-link {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  font-size: var(--font-size-small);
  font-weight: 500;
  padding: 0;
  white-space: nowrap;
  margin-top: 5px !important;
}

.bulk-select-all-link:hover {
  text-decoration: underline;
}

.bulk-select-all-link:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.bulk-center-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.bulk-move-label {
  color: #374151;
  font-size: var(--font-size-small);
  font-weight: 500;
  white-space: nowrap;
}

.bulk-column-select {
  appearance: none;
  -webkit-appearance: none;
  background-color: var(--color-surface);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24'%3E%3Cpath fill='%236b7280' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  color: #374151;
  cursor: pointer;
  font-size: var(--font-size-small);
  height: 4rem;
  min-width: 180px;
  padding: 0 2rem 0 0.75rem;
  width: min(220px, 30vw);
}

.bulk-column-select option {
  font-size: var(--font-size-small);
}

.bulk-column-select:focus,
.bulk-column-select:focus-visible {
  border-color: #93c5fd;
  box-shadow: none;
  outline: none;
}

.bulk-left-group,
.bulk-center-group,
.bulk-right-group {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.bulk-left-group {
  gap: 2rem;
}

.bulk-right-group {
  gap: 0.5rem;
}

.label-actions-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.label-actions-box {
  left: 0;
  right: auto;
  top: calc(100% + 0.5rem);
}

.bulk-confirm-modal {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
}

.bulk-confirm-modal__title {
  font-size: var(--font-size-medium);
  font-weight: 700;
  margin: 0;
}

.bulk-confirm-modal__description {
  color: #374151;
  font-size: var(--font-size-small);
  margin: 0;
}

.bulk-confirm-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.bulk-move-label {
  margin: 0;
  display: flex;
  align-items: center;
}

.bulk-column-select {
  margin: 0;
}

.bulk-actions-bar .button,
.bulk-actions-bar button {
  margin: 0;
}

@media (max-width: 768px) {
  .bulk-actions-bar--active {
    flex-direction: column;
    align-items: flex-start;
  }

  .bulk-column-select {
    min-width: 0;
    width: 100%;
  }
}

.draggable-container {
  display: flex;
}
.board-details .project-name {
  display: flex;
  align-items: center;
}
.board-details .project-name:hover .name-edit-icon {
  display: block;
}
.board-details .name-edit-icon {
  display: none;
  width: 50px;
  text-align: center;
  cursor: pointer;
}
.board-details .project-name-input,
.board-details .project-desc-input {
  padding: 0;
  font-size: 24px;
  color: #525f7f;
  border: 1px solid transparent;
  background: transparent;
  width: 50%;
  padding-left: 10px;
}
.board-details .project-name-input:hover,
.board-details .project-desc-input:hover {
  border: 1px solid #cad1d7;
}
.board-details .project-name-input:focus,
.board-details .project-desc-input:focus {
  border: 1px solid #98a8fb;
}
.board-details .project-desc-input {
  font-size: 15px;
  height: 30px;
}

ul,
li {
  list-style: none;
  padding: 0;
}
.main-container {
  padding-bottom: 0;
  background: var(--color-canvas);
  min-height: 100%;
}

.search-task {
  width: 30%;
  background: none;
  border: none;
  color: #fff;
}
.search-task:focus {
  background: none;
  color: #fff;
}
.search-task.form-control::-webkit-input-placeholder {
  color: white;
}
.bg-gradient-primary-custom {
  background: linear-gradient(89deg, #98a8fb 0, #71b3f8 100%) !important;
}

.board-wrapper {
  display: flex;
  height: 100%;
}

.board-wrapper .task-board {
  background: var(--color-canvas);
  height: auto;
  margin: 15px;
  padding: 15px;
  width: 385px;
  position: relative;
  border-radius: 5px;
  -webkit-box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.21);
  -moz-box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.21);
  box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.21);
}

.task-board:before {
  content: '';
  width: 100%;
  height: 4px;
  background: linear-gradient(89deg, #c875e8 0, #dd6883 100%);
  position: absolute;
  margin-left: -15px;
  border-radius: 5px;
  top: 0;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.task-board.todo-board:before {
  background: linear-gradient(89deg, #c875e8 0, #dd6883 100%);
}

.task-board.inprogress-board:before {
  background: linear-gradient(89deg, #feb143 0, #fe9279 100%);
}

.task-board.review-board:before {
  background: linear-gradient(89deg, #8a92cc 0, #66aef7 100%);
}

.task-board.done-board:before {
  background: linear-gradient(89deg, #75d2a8 0, #78d2ea 100%);
}

.task-board[data-board-name='primary']:before,
.task-board[data-board-name='4']:before {
  background: linear-gradient(89deg, #c875e8 0, #dd6883 100%);
}

.task-board[data-board-name='secondary']:before,
.task-board[data-board-name='5']:before {
  background: linear-gradient(89deg, #feb143 0, #fe9279 100%);
}

.task-board[data-board-name='tertiary']:before,
.task-board[data-board-name='6']:before {
  background: linear-gradient(89deg, #8a92cc 0, #66aef7 100%);
}

.task-board[data-board-name='quaternary']:before,
.task-board[data-board-name='7']:before {
  background: linear-gradient(89deg, #75d2a8 0, #78d2ea 100%);
}

.board-header {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  margin-bottom: 12px;
}

.board-header .board-name {
  font-size: 18px;
  font-weight: 600;
}

.task-item {
  background: var(--color-surface);
  padding: 12px;
  border-radius: 5px;
  margin-bottom: 18px;
  -webkit-box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.31);
  -moz-box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.31);
  box-shadow: 0px 0px 4px 0px rgba(134, 134, 162, 0.31);
}

.task-item-header {
  margin-bottom: 12px;
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
}
.task-item-header p {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 500;
  flex: 1;
  min-width: 0;
}

.task-item-body .task-priority {
  display: inline;
  color: #fff;
  font-size: 13px;
  padding: 3px 18px;
  border-radius: 3px;
  font-weight: 600;
  -webkit-box-shadow: 0px 0px 5px -2px rgba(134, 134, 162, 1);
  -moz-box-shadow: 0px 0px 5px -2px rgba(134, 134, 162, 1);
  box-shadow: 0px 0px 5px -2px rgba(134, 134, 162, 1);
}

.task-priority.low {
  background: linear-gradient(89deg, #66a7f8 0, #8ea7fb 100%);
}

.task-priority.medium {
  background: linear-gradient(89deg, #79d3b9 0, #81d5de 100%);
}

.task-priority.high {
  background: linear-gradient(89deg, #cd74d1 0, #db78a3 100%);
}

.task-item-body {
  margin-bottom: 25px;
}

.task-item-body .task-title {
  color: #8686a2;
  font-weight: bold;
}

.task-item-footer,
.assigned-users,
.comments-attachments {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comments-attachments {
  color: #8686a2;
  font-size: 12px;
}

.comments-attachments i {
  font-size: 12px;
  margin-right: 5px;
}

.comments {
  margin-right: 15px;
}

.user-avatar img {
  border-radius: 50%;
  width: 30px;
}

.assigned-users .add-icon {
  height: 30px;
  width: 30px;
  background: transparent;
  border: 1px solid #8686a2;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 25px;
  font-weight: 300;
  margin-right: 10px;
  color: #8686a2;
}

.board-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
}

.board-footer .add-task-btn {
  color: #8686a2;
  cursor: pointer;
}
.scrollable-div {
  overflow-x: scroll;
}
.card-text {
  color: #525f7f;
  font-weight: 400;
}

.task-item-body .task-title:hover {
  text-decoration: underline;
  cursor: pointer;
}
</style>
